import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Drawer, message, Popconfirm, List, Tag, Alert } from "antd";
import { DeleteOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { InputItem, ButtonItem, InputNumberItem } from "../../../../../Components/Items";

//Panel lateral de un rack (capa física): editar su configuración (columnas/niveles/profundidades), ver a
//qué nodos de la capa de circulación está vinculado (y desvincularlos), y generar sus ubicaciones/bins
//automáticamente. El rack en sí no es parte del grafo de rutas.
const PanelPropiedadesRack = ({ visible, onClose, rack, nodosCrudos, headersToken, onGuardado, onEliminado }) => {
  const [codigo, setCodigo] = useState("");
  const [nombre, setNombre] = useState("");
  const [numeroColumnas, setNumeroColumnas] = useState(1);
  const [numeroNiveles, setNumeroNiveles] = useState(1);
  const [numeroProfundidades, setNumeroProfundidades] = useState(1);
  const [anchoMetros, setAnchoMetros] = useState(undefined);
  const [altoMetros, setAltoMetros] = useState(undefined);
  const [profundidadMetros, setProfundidadMetros] = useState(undefined);
  const [guardando, setGuardando] = useState(false);
  const [ubicaciones, setUbicaciones] = useState([]);
  const [cargandoUbicaciones, setCargandoUbicaciones] = useState(false);
  const [generando, setGenerando] = useState(false);

  useEffect(() => {
    if (rack) {
      setCodigo(rack.codigo || "");
      setNombre(rack.nombre || "");
      setNumeroColumnas(rack.numeroColumnas || 1);
      setNumeroNiveles(rack.numeroNiveles || 1);
      setNumeroProfundidades(rack.numeroProfundidades || 1);
      setAnchoMetros(rack.anchoMetros);
      setAltoMetros(rack.altoMetros);
      setProfundidadMetros(rack.profundidadMetros);
    }
  }, [rack]);

  const cargarUbicaciones = useCallback(async () => {
    if (!rack?._id) return;
    setCargandoUbicaciones(true);
    try {
      const res = await axios.post("rack/selectUbicacionesDeRack", { rackId: rack._id }, { headers: headersToken });
      if (res.data.success === 2) setUbicaciones(res.data.ubicaciones || []);
    } catch (error) {
      console.error("Error al cargar las ubicaciones del rack:", error);
    } finally {
      setCargandoUbicaciones(false);
    }
  }, [rack, headersToken]);

  useEffect(() => {
    if (visible) cargarUbicaciones();
  }, [visible, cargarUbicaciones]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const res = await axios.post(
        "rack/editarRack",
        {
          _id: rack._id,
          codigo,
          nombre,
          x: rack.x,
          y: rack.y,
          ancho: rack.ancho,
          alto: rack.alto,
          numeroColumnas,
          numeroNiveles,
          numeroProfundidades,
          anchoMetros,
          altoMetros,
          profundidadMetros,
        },
        { headers: headersToken }
      );
      if (res.data.success === 2) {
        message.success("Rack actualizado correctamente");
        onGuardado();
      } else {
        message.error(res.data.error || "No se pudo actualizar el rack");
      }
    } catch (error) {
      console.error("Error al actualizar el rack:", error);
      message.error("No se pudo actualizar el rack");
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async () => {
    try {
      const res = await axios.post("rack/eliminarRack", { _id: rack._id }, { headers: headersToken });
      if (res.data.success === 2) {
        message.success("Rack eliminado correctamente");
        onEliminado();
      } else {
        message.error(res.data.error || "No se pudo eliminar el rack");
      }
    } catch (error) {
      console.error("Error al eliminar el rack:", error);
      message.error("No se pudo eliminar el rack");
    }
  };

  const desvincular = async (nodoId) => {
    try {
      const res = await axios.post("rack/desvincularNodoDeRack", { rackId: rack._id, nodoId }, { headers: headersToken });
      if (res.data.success === 2) {
        onGuardado();
      } else {
        message.error(res.data.error || "No se pudo desvincular");
      }
    } catch (error) {
      console.error("Error al desvincular el rack:", error);
      message.error("No se pudo desvincular");
    }
  };

  const generarUbicaciones = async () => {
    setGenerando(true);
    try {
      const res = await axios.post("rack/generarUbicacionesDeRack", { rackId: rack._id }, { headers: headersToken });
      if (res.data.success === 2) {
        message.success(`Se generaron ${res.data.totalGeneradas} ubicaciones`);
        cargarUbicaciones();
      } else {
        message.error(res.data.error || "No se pudieron generar las ubicaciones");
      }
    } catch (error) {
      console.error("Error al generar las ubicaciones del rack:", error);
      message.error("No se pudieron generar las ubicaciones");
    } finally {
      setGenerando(false);
    }
  };

  if (!rack) return null;

  const nodosVinculados = (rack.nodosAccesoIds || []).map((nodoId) => nodosCrudos?.find((n) => String(n._id) === String(nodoId)) || { _id: nodoId, codigo: "?" });

  return (
    <Drawer title="Propiedades del rack" visible={visible} onClose={onClose} width={380}>
      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Código</div>
        <InputItem value={codigo} onChange={(e) => setCodigo(e.target.value)} />
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4, color: "#6b7280" }}>Nombre</div>
        <InputItem value={nombre} onChange={(e) => setNombre(e.target.value)} />
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 4, color: "#6b7280", fontSize: 12 }}>Columnas</div>
          <InputNumberItem style={{ width: "100%" }} min={1} value={numeroColumnas} onChange={setNumeroColumnas} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 4, color: "#6b7280", fontSize: 12 }}>Niveles</div>
          <InputNumberItem style={{ width: "100%" }} min={1} value={numeroNiveles} onChange={setNumeroNiveles} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 4, color: "#6b7280", fontSize: 12 }}>Profundidades</div>
          <InputNumberItem style={{ width: "100%" }} min={1} value={numeroProfundidades} onChange={setNumeroProfundidades} />
        </div>
      </div>

      <div style={{ marginBottom: 4, color: "#6b7280", fontSize: 12 }}>
        Tamaño físico del rack (m) — opcional; sirve para validar que una ubicación no exceda su espacio
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 4, color: "#8a93a6", fontSize: 11 }}>Ancho total (m)</div>
          <InputNumberItem style={{ width: "100%" }} min={0} step={0.1} value={anchoMetros} onChange={setAnchoMetros} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 4, color: "#8a93a6", fontSize: 11 }}>Alto total (m)</div>
          <InputNumberItem style={{ width: "100%" }} min={0} step={0.1} value={altoMetros} onChange={setAltoMetros} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ marginBottom: 4, color: "#8a93a6", fontSize: 11 }}>Prof. total (m)</div>
          <InputNumberItem style={{ width: "100%" }} min={0} step={0.1} value={profundidadMetros} onChange={setProfundidadMetros} />
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
        <Popconfirm title="¿Eliminar este rack?" onConfirm={eliminar} okText="Eliminar" cancelText="Cancelar">
          <ButtonItem danger icon={<DeleteOutlined />}>
            Eliminar
          </ButtonItem>
        </Popconfirm>
        <ButtonItem type="primary" loading={guardando} onClick={guardar}>
          Guardar
        </ButtonItem>
      </div>

      <div style={{ fontWeight: 700, marginBottom: 8 }}>Vinculado a ({nodosVinculados.length})</div>
      {nodosVinculados.length === 0 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Este rack no está vinculado a ningún nodo. Arrastra una conexión desde uno de sus lados hacia un pasillo."
        />
      ) : (
        <List
          style={{ marginBottom: 16 }}
          size="small"
          dataSource={nodosVinculados}
          renderItem={(valorNodo) => (
            <List.Item
              actions={[
                <ButtonItem key="quitar" size="small" danger icon={<DeleteOutlined />} onClick={() => desvincular(valorNodo._id)} />,
              ]}
            >
              {valorNodo.codigo}
            </List.Item>
          )}
        />
      )}

      <div style={{ fontWeight: 700, marginBottom: 8 }}>Ubicaciones ({ubicaciones.length})</div>
      {ubicaciones.length === 0 ? (
        <ButtonItem
          type="primary"
          icon={<ThunderboltOutlined />}
          loading={generando}
          block
          disabled={nodosVinculados.length === 0}
          onClick={generarUbicaciones}
        >
          Generar ubicaciones automáticamente
        </ButtonItem>
      ) : (
        <List
          size="small"
          loading={cargandoUbicaciones}
          dataSource={ubicaciones}
          renderItem={(valorUbicacion) => (
            <List.Item>
              <strong>{valorUbicacion.clave}</strong>
              <Tag style={{ marginLeft: 8 }}>Columna {valorUbicacion.columna}</Tag>
              <Tag>Nivel {valorUbicacion.nivelRack}</Tag>
              <Tag>Prof. {valorUbicacion.profundidad}</Tag>
            </List.Item>
          )}
        />
      )}
    </Drawer>
  );
};

export default PanelPropiedadesRack;
